import { freshnessDate } from "@/lib/currentYear";
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import {
  getCardCategory,
  getOverdraftRate,
  showsCreditMetrics,
  formatPercentDE,
  CARD_CATEGORY_LABEL,
  CARD_CATEGORY_TOOLTIP,
} from "@/lib/cardCategory";
import { annualFeeLabel, categoryLine, quickAnswerText } from "@/lib/cardFacts";
import { CardAtAGlance } from "@/components/CardAtAGlance";
import { QuickAnswer } from "@/components/QuickAnswer";

import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  ArrowRight,
  CheckCircle2, 
  XCircle, 
  Info,
  Calendar,
  ChevronRight
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CardLogo } from "@/components/CardLogo";
import { trackAffiliateClick } from "@/lib/analytics";
import { trackAffiliateClickGTM } from "@/lib/gtm";
import { useAffiliateRedirect } from "@/hooks/useAffiliateRedirect";
import { SectionRenderer } from "@/components/SectionRenderer";
import { renderGermanCardArticle, parseMarkdownBody } from "@/lib/cardTemplateRendererDE";
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { SEOHelmet } from "@/components/SEOHelmet";
import { generateBreadcrumbSchema, generateFAQSchema } from "@/lib/schemaGenerator";
import { cardPageJsonLd } from "@/lib/schema-cards";
import { cardArticleJsonLd } from "@/lib/schema-articles";
import { normalizeDbCard, toRecommendedModalVM, shortPitch, keyLine } from "@/lib/cardCoreNormalizer";
import { formatCardTitle, formatCardDescription } from "@/lib/seoHelpers";
import { SponsoredCardsModule } from "@/components/SponsoredCardsModule";
import { HeroSplitScreen } from "@/components/HeroSplitScreen";
import { toHeroCardVM } from "@/lib/heroCardAdapter";
import { Card as UICard } from "@/components/ui/card";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { CreditCard } from "@/types/card";
import { formatGermanDate } from "@/lib/dateFormat";
import { SITE_ORIGIN } from "@/lib/routeSeo";

/**
 * Capitalize card network names for display
 */
const capitalizeCardNetworks = (text: string | null | undefined): string => {
  if (!text) return '';
  return text
    .replace(/\bvisa\b/gi, 'Visa')
    .replace(/\bmastercard\b/gi, 'Mastercard')
    .replace(/\bamerican express\b/gi, 'American Express')
    .replace(/\bamex\b/gi, 'Amex');
};

interface CardData {
  id: string;
  slug: string;
  name: string;
  display_name?: string;
  issuer: string;
  network: string;
  annual_fee: number;
  apr_effective: number;
  apr_nominal: number;
  credit_limit_min: number;
  credit_limit_max: number;
  interest_free_days: number;
  age_min: number;
  income_min: number;
  apple_pay: boolean;
  google_pay: boolean;
  travel_insurance: boolean;
  apply_url: string;
  affiliate_link: string | null;
  terms_url: string | null;
  logo_url: string | null;
  excerpt: string | null;
  description: string | null;
  benefits: string[];
  drawbacks: string[];
  best_for: string[];
  perks: any;
  selling_points: string[];
  updated_at: string;
  category_id?: string;
}

interface FAQ {
  question: string;
  answer: string;
}

const CardDetail = ({ initialCard }: { initialCard?: any } = {}) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { handleAffiliateRedirect } = useAffiliateRedirect();
  const [card, setCard] = useState<CardData | null>((initialCard as CardData) ?? null);
  const [offer, setOffer] = useState<any>(null);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(!initialCard);
  const [articleHtml, setArticleHtml] = useState<string>("");
  const [isPromoted, setIsPromoted] = useState(false);
  const [ogImageUrl, setOgImageUrl] = useState<string>("");
  const [schemas, setSchemas] = useState<any>({ breadcrumb: null, card: null, faq: null });
  const [ctaSettings, setCtaSettings] = useState({
    promoted_badge_text: "Empfohlen",
    promoted_trust_message: "Sichere Online-Antragstellung über {bank}. Antwort in wenigen Minuten.",
    promoted_button_text: "Jetzt beantragen",
    promoted_disclaimer_title: "Anzeige:",
    promoted_disclaimer_text: "Dieses Angebot wird hervorgehoben, da wir eine Provision erhalten, wenn Sie über unseren Link beantragen. Dies beeinflusst nicht die Informationen, die wir über das Produkt bereitstellen.",
    standard_button_text: "Jetzt beantragen",
    standard_redirect_message: "Sie werden zu {bank} weitergeleitet",
    info_section_title: "Wichtige Informationen",
    show_similar_cards: true,
  });
  const [sellingPoints, setSellingPoints] = useState<string[]>([]);
  const { toast } = useToast();

  // Derive normalized view models for consistent text (must be before early returns)
  const normalizedCore = useMemo(() => (card ? normalizeDbCard(card as any) : null), [card]);
  const recommendedVM = useMemo(() => (normalizedCore ? toRecommendedModalVM(normalizedCore) : null), [normalizedCore]);
  
  // Adapt normalized core data for HeroSplitScreen (with logo)
  const heroCardData = useMemo(() => 
    normalizedCore ? toHeroCardVM(normalizedCore, card?.logo_url, card) : null, 
    [normalizedCore, card?.logo_url, card]
  );

  // Parallelize all data fetching to eliminate sequential loading waterfall
  useEffect(() => {
    if (!slug) return;
    
    const fetchAllData = async () => {
      try {
        // Phase 1: Fetch independent data in parallel
        const [cardResult, ctaResult, templateResult] = await Promise.all([
          // Fetch card data
          supabase
            .from("cards")
            .select("*")
            .eq("slug", slug)
            .eq("status", "published")
            .single(),
          
          // Fetch CTA settings
          supabase
            .from("page_settings")
            .select("settings")
            .eq("page_type", "card_detail")
            .single(),
          
          // Fetch card template (for article rendering)
          supabase
            .from('card_templates')
            .select('*')
            .eq('is_active', true)
            .maybeSingle()
        ]);

        if (cardResult.error) throw cardResult.error;
        const cardData = cardResult.data;
        setCard(cardData as CardData);

        // Process CTA settings
        if (ctaResult.data?.settings && typeof ctaResult.data.settings === 'object') {
          const settings = ctaResult.data.settings as any;
          if ('faq' in settings) {
            setFaqs(settings.faq || []);
          }
          if ('cta' in settings) {
            setCtaSettings(prev => ({ ...prev, ...settings.cta }));
          }
        }

        // Phase 2: Fetch dependent data in parallel (now that we have card data)
        const [promotedResult, offerResult, ogSettingsResult, siteSettingsResult] = await Promise.all([
          // Check if promoted
          supabase
            .from("ad_placements")
            .select("card_id")
            .eq("card_id", cardData.id)
            .or(`start_at.is.null,start_at.lte.${new Date().toISOString()}`)
            .or(`end_at.is.null,end_at.gte.${new Date().toISOString()}`)
            .maybeSingle(),
          
          // Fetch offers
          supabase
            .from("offers")
            .select("*")
            .eq("card_id", cardData.id)
            .lte("start_at", new Date().toISOString())
            .or(`end_at.is.null,end_at.gte.${new Date().toISOString()}`)
            .maybeSingle(),
          
          // Fetch OG image settings
          supabase
            .from('og_image_settings')
            .select('*')
            .single(),
          
          // Fetch site settings for schemas
          supabase
            .from('page_settings')
            .select('settings')
            .eq('page_type', 'site_settings')
            .single()
        ]);

        // Paid placement only — an affiliate link alone is not a recommendation
        const isInAdPlacements = !!promotedResult.data;
        setIsPromoted(isInAdPlacements);


        // Set offer
        setOffer(offerResult.data);

        // Generate OG image URL
        const domain = window.location.origin;
        const ogParams = new URLSearchParams({
          type: 'card',
          slug: cardData.slug,
        });

        if (ogSettingsResult.data?.background_image_url) {
          ogParams.append('background', ogSettingsResult.data.background_image_url);
        }
        if (ogSettingsResult.data?.fallback_card_image_url) {
          ogParams.append('fallbackCard', ogSettingsResult.data.fallback_card_image_url);
        }
        if (ogSettingsResult.data?.fallback_site_image_url) {
          ogParams.append('fallbackOg', ogSettingsResult.data.fallback_site_image_url);
        }
        
        const cacheVersion = cardData.updated_at 
          ? new Date(cardData.updated_at).getTime() 
          : Date.now();
        ogParams.append('v', cacheVersion.toString());

        setOgImageUrl(`${domain}/functions/v1/generate-og-image-v2?${ogParams.toString()}`);

        // Render article from template
        if (templateResult.data) {
          const renderedMarkdown = renderGermanCardArticle(
            templateResult.data.article_template,
            cardData as any
          );
          const markdownBody = parseMarkdownBody(renderedMarkdown);
          const html = await marked(markdownBody);
          const sanitized = DOMPurify.sanitize(html);
          setArticleHtml(sanitized);
        }

        // Generate schemas (must be done after we have all data)
        const normalizedData = normalizeDbCard(cardData as any);
        const cardUrl = `${domain}/kreditkarten/${cardData.slug}`;
        const settings = siteSettingsResult.data?.settings as any;
        const organizationName = settings?.organization_name || 'Kreditkarten Vergleich';
        
        const keywords = [
          "Kreditkarte",
          cardData.issuer,
          cardData.network,
          ...(cardData.best_for || []),
          ...(cardData.annual_fee === 0 ? ["kostenlos", "gebührenfrei"] : []),
          ...(cardData.travel_insurance ? ["Reiseversicherung"] : [])
        ].filter(Boolean).slice(0, 8);
        
        const cardSchemas = cardPageJsonLd({
          core: normalizedData,
          url: cardUrl,
          imageUrl: cardData.logo_url || undefined,
          breadcrumb: [
            { name: 'Startseite', url: domain },
            { name: cardData.display_name || cardData.name, url: cardUrl }
          ],
          currency: 'EUR'
        });
        
        const articleSchema = cardArticleJsonLd({
          url: cardUrl,
          headline: `Bewertung von ${normalizedData.name}`,
          description: shortPitch(normalizedData) || keyLine(normalizedData) || "",
          image: cardData.logo_url || undefined,
          authorName: settings?.default_author || "Redaktion",
          datePublished: cardData.updated_at || new Date().toISOString(),
          breadcrumbs: [
            { name: 'Startseite', url: domain },
            { name: cardData.display_name || cardData.name, url: cardUrl }
          ],
          aboutCreditCardId: `${cardUrl}#creditcard`,
          organizationName: organizationName
        });
        
        // FAQs are already set from CTA settings above
        const ctaSettings = ctaResult.data?.settings as any;
        const currentFaqs = ctaSettings?.faq || [];
        const faqSchema = currentFaqs.length > 0 ? await generateFAQSchema(currentFaqs) : null;
        
        setSchemas({ 
          breadcrumb: cardSchemas[1], 
          card: [...cardSchemas, articleSchema], 
          faq: faqSchema 
        });

      } catch (error: any) {
        console.error('Error fetching card data:', error);
        toast({
          title: "Karte konnte nicht geladen werden",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [slug]);

  // Fetch selling points from overrides when card is promoted
  useEffect(() => {
    if (card && isPromoted) {
      fetchSellingPointsFromOverrides();
    }
  }, [card, isPromoted]);

  const fetchSellingPointsFromOverrides = async () => {
    if (!card) return;
    
    try {
      const { data, error } = await supabase
        .from('article_ad_card_overrides')
        .select(`
          selling_point_1,
          selling_point_2,
          article_ad_configs!inner(is_active)
        `)
        .eq('card_id', card.id)
        .eq('article_ad_configs.is_active', true)
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        const points = [data.selling_point_1, data.selling_point_2].filter(Boolean);
        setSellingPoints(points);
      }
    } catch (error) {
      console.error('Error fetching selling points:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <Header onNavigate={() => {}} currentPage="card-detail" />
        
        <section className="bg-gradient-to-b from-gray-50 to-white py-4 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb skeleton */}
            <nav className="mb-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-48" />
              </div>
            </nav>
            
            {/* Hero card skeleton */}
            <div className="overflow-hidden shadow-xl border border-gray-200 rounded-lg">
              <div className="flex flex-col lg:flex-row min-h-[400px]">
                {/* Left panel - card image */}
                <div className="w-full lg:w-1/2 bg-gradient-to-br from-slate-100 to-gray-100 flex items-center justify-center p-8 lg:p-12">
                  <div className="flex flex-col items-center w-full">
                    <Skeleton className="w-72 h-48 rounded-2xl mb-6" />
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Skeleton className="h-8 w-40 rounded-full" />
                      <Skeleton className="h-8 w-36 rounded-full" />
                      <Skeleton className="h-8 w-44 rounded-full" />
                    </div>
                  </div>
                </div>
                
                {/* Right panel - card info */}
                <div className="w-full lg:w-1/2 bg-white flex items-center p-8 lg:p-12">
                  <div className="max-w-md w-full">
                    <Skeleton className="h-6 w-24 mb-4 rounded-full" />
                    <Skeleton className="h-8 w-full mb-3" />
                    <Skeleton className="h-4 w-3/4 mb-6" />
                    
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                    </div>
                    
                    <Skeleton className="h-12 w-full rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <Footer onNavigate={() => {}} />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-gray-900 mb-2">Karte nicht gefunden</h2>
          <Link to="/">
            <Button variant="outline">
              Zurück zur Übersicht
            </Button>
          </Link>
        </div>
      </div>
    );
  }


  // Determine CTA button visibility and content
  const affiliateUrl = offer?.apply_url || card.affiliate_link;
  const hasAffiliate = !!affiliateUrl;
  const ctaUrl = affiliateUrl || card.apply_url;
  const ctaText = hasAffiliate ? (offer?.title || 'Jetzt bestellen') : 'Jetzt beantragen';

  const handleNavigate = (page: string) => {
    if (page === 'home') {
      navigate('/');
    }
  };

  // Flatten all schemas into a single array
  const allSchemas = [
    ...(schemas.card || []),  // This is the array from cardPageJsonLd
    schemas.breadcrumb,
    schemas.faq
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* SEO Meta Tags */}
      <SEOHelmet
        title={formatCardTitle(
          (card as any).display_name || card.name,
          card.apr_effective,
          card.annual_fee
        )}
        description={formatCardDescription(
          shortPitch(normalizedCore!) || card.excerpt || `Kreditkarte mit attraktiven Vorteilen`,
          (card as any).display_name || card.name
        )}
        url={`${SITE_ORIGIN}/kreditkarten/${card.slug}`}
        image={ogImageUrl}
        type="product"
        productBrand={card.issuer}
        productPrice={card.annual_fee}
        modifiedTime={freshnessDate(card.updated_at)}
        additionalSchemas={allSchemas}
        preloadImage={heroCardData?.imageUrl ?? undefined}
      />
      
      {/* Header */}
      <Header onNavigate={handleNavigate} currentPage="card-detail" />
      
{/* ===== BREADCRUMB + HERO SECTION ===== */}
<section className="bg-gradient-to-b from-gray-50 to-white py-4 border-b border-gray-200">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    
    {/* Breadcrumb Navigation */}
    <nav className="mb-3">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Alle Kreditkarten
                </Link>
              </li>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <li className="text-gray-900 font-medium">
                {card.display_name || card.name}
              </li>
            </ol>
          </nav>

          {/* Hero Card Container */}
          <UICard className="overflow-hidden shadow-xl border-gray-200">
            {heroCardData && (
            <HeroSplitScreen
              card={heroCardData}
              cardDetailUrl={`/kreditkarten/${card.slug || card.id}`}
              isPromoted={isPromoted}
              isLoading={loading}
              hasAffiliateLink={hasAffiliate}
              ctaHref={affiliateUrl || undefined}
              displayConfigChips={(card as any)?.display_config?.chips || []}
              isGerman={!!(card as any)?.is_prepaid || !!(card as any)?.network}
              onCTAClick={async () => {
                if (affiliateUrl) {
                  // Track in both Supabase and GTM
                  trackAffiliateClick(card.id, 'detail_page', card.name);
                  await trackAffiliateClickGTM({
                    cardId: card.id,
                    cardName: card.display_name || card.name,
                    issuerName: card.issuer,
                    affiliateUrl: affiliateUrl,
                    clickSource: 'detail_page',
                    offerTitle: offer?.title,
                    annualFee: card.annual_fee,
                    effectiveRate: card.apr_effective,
                  });
                  handleAffiliateRedirect({
                    cardId: card.id,
                    cardName: card.display_name || card.name,
                    issuerName: card.issuer,
                    affiliateUrl: affiliateUrl,
                    source: 'detail_page'
                  });
                }
              }}
            />
            )}
          </UICard>
        </div>
      </section>

      {/* ===== MAIN CONTENT ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* ===== VENSTRE KOLONNE - Main Content ===== */}
          <div className="lg:col-span-2 space-y-8">

            {/* KURZANTWORT + AUF EINEN BLICK (quotable, data-backed) */}
            <QuickAnswer text={quickAnswerText(card)} />
            <CardAtAGlance card={card} />

            {/* RENDERED ARTICLE FROM TEMPLATE */}
            {articleHtml ? (
              <section className="bg-white rounded-xl p-6 border border-gray-200">
                <div 
                  className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900"
                  dangerouslySetInnerHTML={{ __html: articleHtml }}
                />
              </section>
            ) : (
              <>
                {/* FALLBACK: Original hardcoded sections if template not available */}
                {(card.description || card.excerpt) && (
                  <section className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Über die Karte</h2>
                    <p className="text-gray-700 leading-relaxed">{card.description || card.excerpt}</p>
                  </section>
                )}

                {card.benefits && card.benefits.length > 0 && (
                  <section className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Vorteile</h2>
                    <ul className="space-y-3">
                      {card.benefits.map((pro, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {card.drawbacks && card.drawbacks.length > 0 && (
                  <section className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Nachteile</h2>
                    <ul className="space-y-3">
                      {card.drawbacks.map((con, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{con}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {card.best_for && card.best_for.length > 0 && (
                  <section className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Geeignet für</h2>
                    <ul className="space-y-3">
                      {card.best_for.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}

            {/* NEW SECTIONS (Phase 5) */}
            
            {/* Foreign Fees & ATM Section */}
            {((card as any).display_config?.fxTableText || (card as any).display_config?.atmTableText) && (
              <UICard className="p-6">
                <h3 className="text-xl font-semibold mb-4">Gebühren im Ausland</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {(card as any).display_config?.fxTableText && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        Auslandseinsatzentgelt
                      </h4>
                      <p className="text-sm text-gray-600">{(card as any).display_config.fxTableText}</p>
                    </div>
                  )}
                  {(card as any).display_config?.atmTableText && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        Abhebungsgebühren
                      </h4>
                      <p className="text-sm text-gray-600">{(card as any).display_config.atmTableText}</p>
                    </div>
                  )}
                </div>
              </UICard>
            )}

            {/* Travel Insurance Grid */}
            {((card as any).travel_health_insurance || (card as any).travel_accident_insurance || 
              (card as any).travel_cancellation_insurance || (card as any).travel_baggage_insurance) && (
              <UICard className="p-6">
                <h3 className="text-xl font-semibold mb-4">Versicherungen – Kurzüberblick</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex flex-col items-center text-center gap-2">
                    {(card as any).travel_health_insurance ? 
                      <CheckCircle2 className="w-8 h-8 text-green-600" /> : 
                      <XCircle className="w-8 h-8 text-gray-300" />
                    }
                    <span className="text-sm font-medium">Krankenversicherung</span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-2">
                    {(card as any).travel_accident_insurance ? 
                      <CheckCircle2 className="w-8 h-8 text-green-600" /> : 
                      <XCircle className="w-8 h-8 text-gray-300" />
                    }
                    <span className="text-sm font-medium">Unfallversicherung</span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-2">
                    {(card as any).travel_cancellation_insurance ? 
                      <CheckCircle2 className="w-8 h-8 text-green-600" /> : 
                      <XCircle className="w-8 h-8 text-gray-300" />
                    }
                    <span className="text-sm font-medium">Reiserücktritt</span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-2">
                    {(card as any).travel_baggage_insurance ? 
                      <CheckCircle2 className="w-8 h-8 text-green-600" /> : 
                      <XCircle className="w-8 h-8 text-gray-300" />
                    }
                    <span className="text-sm font-medium">Gepäckversicherung</span>
                  </div>
                </div>
              </UICard>
            )}

            {/* Card Type & Repayment */}
            <UICard className="p-6">
              <h3 className="text-xl font-semibold mb-4">Kartentyp & Rückzahlung</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-gray-700">Kartentyp:</span>
                  <div className="text-right">
                    <p className="font-medium">{CARD_CATEGORY_LABEL[getCardCategory(card)]}</p>
                    {getCardCategory(card) === 'prepaid' && (
                      <Badge variant="secondary" className="mt-1">Prepaid (Aufladen nötig)</Badge>
                    )}
                    {getCardCategory(card) === 'revolving' && (
                      <Badge variant="secondary" className="mt-1">Teilzahlung möglich</Badge>
                    )}
                    {getCardCategory(card) === 'charge' && (
                      <Badge variant="secondary" className="mt-1">Monatlicher Vollausgleich</Badge>
                    )}
                    {getCardCategory(card) === 'debit' && (
                      <Badge variant="secondary" className="mt-1">Direkte Kontobelastung</Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">{CARD_CATEGORY_TOOLTIP[getCardCategory(card)]}</p>
                {showsCreditMetrics(card) && card.apr_effective && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Effektiver Jahreszins:</span>
                    <span className="font-medium">{formatPercentDE(card.apr_effective)}</span>
                  </div>
                )}
                {getCardCategory(card) === 'debit' && getOverdraftRate(card) !== null && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Dispozins des Girokontos:</span>
                    <span className="font-medium">{formatPercentDE(getOverdraftRate(card)!)}</span>
                  </div>
                )}
                {(card as any).current_account_included && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Girokonto:</span>
                    <Badge variant="outline">Mit Girokonto</Badge>
                  </div>
                )}
                {(card as any).nfc_contactless && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Kontaktlos (NFC):</span>
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
            </UICard>

            {/* Liability & Emergency */}
            {(card as any).hotline_available && (
              <UICard className="p-6">
                <h3 className="text-xl font-semibold mb-4">Haftung & Notfall</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">24/7 Notfall-Hotline verfügbar</span>
                  </div>
                </div>
              </UICard>
            )}

            {/* Discounts & Partners */}
            {card.description && (card.description.includes('Rabatte') || card.description.includes('5 %')) && (
              <UICard className="p-6">
                <h3 className="text-xl font-semibold mb-4">Rabatte & Partner</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  {(() => {
                    const parts = card.description.split('\n\n');
                    const discountPart = parts.find(p => 
                      p.includes('Rabatte') || 
                      p.includes('Discount') || 
                      p.includes('5 %')
                    );
                    if (discountPart) {
                      return discountPart.split('\n').map((line: string, i: number) => (
                        <p key={i}>{line}</p>
                      ));
                    }
                    return null;
                  })()}
                </div>
              </UICard>
            )}

            {/* FAQ ACCORDION */}
            <section className="bg-white rounded-xl p-6 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Häufig gestellte Fragen (FAQ)</h2>
              {faqs.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>{faq.question}</AccordionTrigger>
                      <AccordionContent>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <p className="text-gray-500 text-center py-4">Ingen spørsmål tilgjengelig</p>
              )}
            </section>

            {/* LAST UPDATED */}
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              <span>
                {!!(card as any)?.is_prepaid || !!(card as any)?.network
                  ? `Zuletzt aktualisiert: ${formatGermanDate(card.updated_at)}`
                  : `Sist oppdatert: ${new Date(card.updated_at).toLocaleDateString('nb-NO', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}`
                }
              </span>
            </div>
          </div>

          {/* ===== HØYRE KOLONNE - Sticky CTA & Info Panel ===== */}
          <div className="lg:col-span-1">
            {isPromoted ? (
              // Promoted Card Design - Premium CTA
              <div className="sticky top-24 space-y-4">
                <div className="bg-white rounded-xl overflow-hidden border border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600" />
                  
                  <div className="p-6 space-y-5">
                    <div className="flex flex-wrap justify-center gap-2">
                      <Badge variant="outline">{categoryLine(card).label}</Badge>
                      <Badge className="bg-blue-50 text-blue-800 border-blue-200">
                        {annualFeeLabel(card)} / Jahr
                      </Badge>
                    </div>


                    <div className="flex justify-center">
                      <div className="w-48 h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl overflow-hidden shadow-md border border-gray-200">
                        <CardLogo logoUrl={card.logo_url} bankName={card.issuer} className="w-full h-full object-cover" />
                      </div>
                    </div>

                    <div className="text-center">
                      <h3 className="text-gray-900 mb-1 font-semibold">{recommendedVM?.title || card.display_name || card.name}</h3>
                      <p className="text-sm text-gray-500">{recommendedVM?.issuer || card.issuer}</p>
                    </div>

                    {((sellingPoints.length > 0 ? sellingPoints : (recommendedVM?.bullets || [])).length > 0) && (
                      <div className="space-y-2.5 pt-2">
                        {(sellingPoints.length > 0 ? sellingPoints : recommendedVM!.bullets).map((point, idx) => (
                          <div key={idx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700 leading-snug">{point}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Jahresgebühr</p>
                        <p className="text-gray-900 font-semibold">
                          {recommendedVM?.facts.fee
                            ? (recommendedVM.facts.fee === "Gratis"
                                ? <span className="text-green-600">Gratis</span>
                                : recommendedVM.facts.fee)
                            : (card.annual_fee === 0 ? <span className="text-green-600">Gratis</span> : `${card.annual_fee.toLocaleString('de-DE')} €`)
                          }
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">
                          {showsCreditMetrics(card) ? 'Eff. Zinssatz' : 'Kartentyp'}
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {showsCreditMetrics(card)
                            ? (recommendedVM?.facts.rate || (card.apr_effective != null ? formatPercentDE(card.apr_effective) : '–'))
                            : CARD_CATEGORY_LABEL[getCardCategory(card)]
                          }
                        </p>
                      </div>
                    </div>

                    {hasAffiliate && (
                      <>
                        <Button 
                          size="lg"
                          className="w-full gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                          onClick={async (e) => {
                            e.preventDefault();
                            // Track in both Supabase and GTM
                            trackAffiliateClick(card.id, 'detail_page', card.name);
                            await trackAffiliateClickGTM({
                              cardId: card.id,
                              cardName: card.display_name || card.name,
                              issuerName: card.issuer,
                              affiliateUrl: ctaUrl,
                              clickSource: 'detail_page',
                              offerTitle: offer?.title,
                              annualFee: card.annual_fee,
                              effectiveRate: card.apr_effective,
                            });
                            handleAffiliateRedirect({
                              cardId: card.id,
                              cardName: card.display_name || card.name,
                              issuerName: card.issuer,
                              affiliateUrl: ctaUrl,
                              source: 'detail_page',
                              offerTitle: offer?.title,
                              annualFee: card.annual_fee,
                              effectiveRate: card.apr_effective,
                            });
                          }}
                        >
                          {ctaSettings.promoted_button_text}
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>

                        <p className="text-xs text-gray-500 text-center leading-relaxed">
                          {ctaSettings.promoted_trust_message.replace('{bank}', card.issuer)}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                 <div className="flex justify-center">
                   <Badge variant="outline" className="text-xs">
                     Anzeige
                   </Badge>
                 </div>

                {/* TEMPORARILY HIDDEN: Nøkkelinformasjon - redundant with hero section
                <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">{ctaSettings.info_section_title}</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Effektiv rente</div>
                      <div className="text-lg font-bold text-gray-900">{card.apr_effective.toFixed(2)}%</div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Nominell rente</div>
                      <div className="text-gray-700">{card.apr_nominal.toFixed(2)}%</div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Årsgebyr</div>
                      <div className="text-gray-700">{card.annual_fee === 0 ? 'Gratis' : `${card.annual_fee.toLocaleString('nb-NO')} kr`}</div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Rentefri periode</div>
                      <div className="text-gray-700">{card.interest_free_days} dager</div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Kredittgrense</div>
                      <div className="text-gray-700">
                        {card.credit_limit_min && card.credit_limit_max 
                          ? `${card.credit_limit_min.toLocaleString('nb-NO')} - ${card.credit_limit_max.toLocaleString('nb-NO')} kr`
                          : 'Ikke oppgitt'}
                      </div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Nettverk</div>
                      <div className="text-gray-700">{capitalizeCardNetworks(card.network)}</div>
                    </div>

                    <div className="h-px bg-gray-200"></div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Alderskrav</div>
                      <div className="text-gray-700">{card.age_min} år</div>
                    </div>

                    {card.income_min > 0 && (
                      <>
                        <div className="h-px bg-gray-200"></div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Inntektskrav</div>
                          <div className="text-gray-700">{card.income_min.toLocaleString('nb-NO')} kr</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                */}
              </div>
            ) : (
              // Standard Card Design - Show sponsored cards module
              <div className="sticky top-24">
                <SponsoredCardsModule 
                  articleId={card.id}
                  categoryId={card.category_id || undefined}
                />
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ===== STICKY MOBILE CTA (kun mobil) ===== */}
      <StickyMobileCTA
        card={{
          id: card.id,
          name: card.display_name || card.name,
          bank: card.issuer,
          logo: card.logo_url || '',
          effectiveRate: card.apr_effective,
          annualFee: card.annual_fee,
          interestFreePeriod: card.interest_free_days || 0,
          network: card.network || '',
          creditLimit: '',
          minAge: card.age_min || 18,
          features: [],
          badges: [],
          isSponsored: false,
          pros: [],
          cons: [],
          suitableFor: [],
          lastUpdated: card.updated_at || '',
          nominalRate: card.apr_nominal || 0,
          travelInsurance: false,
          sellingPoints: []
        } as CreditCard}
        cardId={card.id}
        affiliateLink={ctaUrl}
      />

      {/* Dynamic Sections */}
      <SectionRenderer
        currentPage="card-detail"
        onViewDetails={(slug) => navigate(`/kreditkarten/${slug}`)}
        onViewAllCards={() => navigate("/")}
      />

      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default CardDetail;
